import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import API_BASE_URL from "../config/appConfig";

/**
 * The signed-in user, fetched once and shared.
 *
 * The access token carries only name/email/id, so anything stored on the user
 * record — the avatar included — has to come from the API. Both the profile
 * page and the header need it, and a change made on one has to show on the
 * other immediately, so it lives here rather than being fetched twice.
 */

const CurrentUserContext = createContext(null);

const readToken = () => {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;
  try {
    return { token, claims: jwtDecode(token) };
  } catch {
    return null;
  }
};

export const CurrentUserProvider = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem("accessToken"));

  // Mirrors `user` so updateUser can roll back without depending on it.
  const userRef = useRef(null);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const refresh = useCallback(async () => {
    const auth = readToken();
    if (!auth?.claims?.id) {
      setUser(null);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/users/getUser/${auth.claims.id}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });
      // Fall back to the token's claims for anything the record omits.
      const merged = { ...auth.claims, ...data, id: auth.claims.id };
      setUser(merged);
      return merged;
    } catch (error) {
      // A failed fetch should not blank the header — keep the token's claims.
      setUser((prev) => prev || auth.claims);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * PUT a partial update through the profile API and adopt the response.
   *
   * Applies the change locally first so the page and the header both move the
   * instant the user acts, and rolls back if the request fails.
   */
  const updateUser = useCallback(async (patch) => {
    const auth = readToken();
    if (!auth?.claims?.id) throw new Error("Not signed in");

    const previous = userRef.current;
    setUser((prev) => ({ ...(prev || auth.claims), ...patch }));

    try {
      const { data } = await axios.put(
        `${API_BASE_URL}/users/updateUser/${auth.claims.id}`,
        patch,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );

      /* Mongoose drops fields the schema does not declare, so the API can
         answer 200 having stored nothing. Without this check the UI would
         report success and the value would vanish on the next refresh. */
      const dropped = Object.keys(patch).filter(
        (key) => patch[key] !== "" && data && !(key in data)
      );
      if (dropped.length) {
        throw new Error(
          `The server accepted the request but did not store: ${dropped.join(", ")}. ` +
          `The API is probably running an older build.`
        );
      }

      setUser((prev) => ({ ...(prev || auth.claims), ...data, id: auth.claims.id }));
      return data;
    } catch (error) {
      setUser(previous);
      throw error;
    }
  }, []);

  /* Signing in and out both navigate, so a route change is the cue to re-read
     the token. Comparing values first means we only refetch when it actually
     changed, not on every navigation. */
  useEffect(() => {
    const current = localStorage.getItem("accessToken");
    setToken((prev) => (prev === current ? prev : current));
  }, [location.pathname]);

  useEffect(() => {
    refresh();
  }, [refresh, token]);

  const value = useMemo(
    () => ({ user, loading, refresh, updateUser }),
    [user, loading, refresh, updateUser]
  );

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};

export const useCurrentUser = () => {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) {
    // A missing provider should degrade, not crash a page.
    return { user: null, loading: false, refresh: async () => null, updateUser: async () => {} };
  }
  return ctx;
};

export default CurrentUserProvider;
